/**
 * Shared date helpers for Profit Report, Truth Profitability, and Util Report (zeecore v2).
 */

import { parseLegacyMonYyRange } from './formatPeriodLabel';

/** Local calendar date → `YYYY-MM-DD`. */
export function formatLocalDateIso(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Local calendar date → `YYYYMMDD` (batch API suffix segment). */
export function formatLocalDateCompact(d: Date): string {
  return formatLocalDateIso(d).replace(/-/g, '');
}

/** Inclusive end of the live batch reporting window: yesterday (today's ingest is usually incomplete). */
export function getYesterdayLocalCompact(): string {
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  return formatLocalDateCompact(yesterday);
}

/** Default truth-layer window: last 7 calendar days including today. */
export function getDefaultTruthDateRange(): { startDate: string; endDate: string } {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - 6);
  return {
    startDate: formatLocalDateIso(start),
    endDate: formatLocalDateIso(end),
  };
}

export function isoToCompact(iso: string): string {
  return iso.replace(/-/g, '');
}

/** Parse batch suffix `20260101_to_20260419` → ISO dates. */
export function parseProfitabilityDateRangeSuffix(suffix: string): { startDate: string; endDate: string } {
  const [startRaw, endRaw] = suffix.split('_to_');
  const toIso = (compact: string) => {
    if (!compact || !/^\d{8}$/.test(compact)) return compact;
    return `${compact.slice(0, 4)}-${compact.slice(4, 6)}-${compact.slice(6, 8)}`;
  };
  return { startDate: toIso(startRaw), endDate: toIso(endRaw) };
}

/** Batch API suffix from ISO dates. */
export function toBatchDateRangeSuffix(startDate: string, endDate: string): string {
  return `${isoToCompact(startDate)}_to_${isoToCompact(endDate)}`;
}

/** Ensure start ≤ end (adjust end down to start when inverted). */
export function clampDateRange(
  startDate: string,
  endDate: string,
): { startDate: string; endDate: string } {
  if (startDate > endDate) {
    return { startDate, endDate: startDate };
  }
  return { startDate, endDate };
}

const compactToIso = (compact: string): string => {
  if (!compact || !/^\d{8}$/.test(compact)) return compact;
  return `${compact.slice(0, 4)}-${compact.slice(4, 6)}-${compact.slice(6, 8)}`;
};

/** Parse batch/export period string → inclusive ISO date range. */
export function parsePeriodToIsoDateRange(period: string): { startDate: string; endDate: string } | null {
  const p = period.trim();
  const match = p.match(/^(\d{8})_to_(\d{8})/i) || p.match(/^(\d{8})\s+to\s+(\d{8})/i);
  if (match) {
    return {
      startDate: compactToIso(match[1]),
      endDate: compactToIso(match[2]),
    };
  }
  if (/^\d{8}$/.test(p)) {
    const iso = compactToIso(p);
    return { startDate: iso, endDate: iso };
  }
  const legacy = parseLegacyMonYyRange(p);
  if (legacy) {
    return {
      startDate: compactToIso(legacy.startYmd),
      endDate: compactToIso(legacy.endYmd),
    };
  }
  return null;
}

/** ISO range for truth utilisation fetch on Util tab (month button or exported periods). */
export function getUtilTruthFetchDateRange(
  period: string,
  periodsToFetch: string[],
  selectedMonth: { year: number; month: number } | null,
): { startDate: string; endDate: string } | null {
  if (selectedMonth) {
    const y = selectedMonth.year;
    const m = selectedMonth.month;
    const lastDay = new Date(y, m, 0).getDate();
    const mm = String(m).padStart(2, '0');
    return {
      startDate: `${y}-${mm}-01`,
      endDate: `${y}-${mm}-${String(lastDay).padStart(2, '0')}`,
    };
  }
  const ranges = periodsToFetch
    .map((p) => parsePeriodToIsoDateRange(p))
    .filter((r): r is { startDate: string; endDate: string } => r != null);
  if (ranges.length === 0) return parsePeriodToIsoDateRange(period);
  return {
    startDate: ranges.reduce((min, r) => (r.startDate < min ? r.startDate : min), ranges[0].startDate),
    endDate: ranges.reduce((max, r) => (r.endDate > max ? r.endDate : max), ranges[0].endDate),
  };
}
